import React, { ReactNode, useContext } from 'react';
import styled from 'styled-components';

import ExpandCollapseButton from 'src/presentation/components/pages/sidekick/threads/components/ExpandCollapseButton';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import { ParsedSpec, parseSpecRecursive } from './memoryViewHelper';
import { StyledGrayContainer } from 'src/presentation/components/pages/sidekick/threads/SidekickLayout';
import { ThreadModuleContext } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ThreadModuleContext';
import { Collapsed, Tooltip } from 'src/presentation/components/agent_desktop_components';
import { StyledContent } from '@ad-scripts/presentation/components/common/collapse';

export default function MemoryExplorer() {
  const { module } = useContext(ThreadModuleContext);

  if (!module?.externalAction?.results?.length) {
    return null;
  }

  const spec = module?.externalAction?.results[0].typeSpec;
  const tree = parseSpecRecursive(spec);

  return (
    <FieldContainer
      control={
        <MemoryExplorerContainer>
          <TypeSpecTree spec={tree[0]} />
        </MemoryExplorerContainer>
      }
    />
  );
}

type TypeSpecTreeProps = {
  spec: ParsedSpec;
  level?: number;
};
const TypeSpecTree = ({ spec, level = 0 }: TypeSpecTreeProps) => {
  const renderChildren = (children: ParsedSpec[] | undefined, level: number) => {
    if (!children) {
      return null;
    }

    return children.map((child, index) => <TypeSpecTree key={index} spec={child} level={level} />);
  };

  if (!spec.name && !spec.children?.length) {
    return null;
  }

  if (!spec.name) {
    return <>{renderChildren(spec.children, level + 1)}</>;
  }

  let name: string | ReactNode = spec.isArray ? `[ ] ${spec.name}` : spec.name;
  if (!spec.nullable) {
    name = (
      <>
        {name}
        <NotNullMarker />
      </>
    );
  }

  return (
    <TypeSpecTreeValue name={name} level={level + 1}>
      {renderChildren(spec.children, level + 1)}
    </TypeSpecTreeValue>
  );
};

const NotNullMarker = () => {
  return (
    <Tooltip message="Not null">
      <NotNullMarkerContainer>!</NotNullMarkerContainer>
    </Tooltip>
  );
};

const NotNullMarkerContainer = styled.span`
  cursor: default;
  padding-left: 4px;
`;

type TypeSpecTreeValueProps = {
  name: string | ReactNode;
  children?: ReactNode;
  level: number;
};

const TypeSpecTreeValue = ({ name, children, level }: TypeSpecTreeValueProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <NameContainer>
        <ExpandCollapseButton hasChildren={!!children} isOpen={isOpen} setIsOpen={setIsOpen} />
        {name}
      </NameContainer>

      <ChildrenContainer isActive={isOpen} level={level + 1} className="collapsibleContainer">
        {children}
      </ChildrenContainer>
    </>
  );
};

const NameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ChildrenContainer = styled(Collapsed)`
  border: 0;
  border-radius: 0;
  background-color: transparent;

  ${StyledContent} {
    padding: 0 16px;
  }
`;

const MemoryExplorerContainer = styled.div`
  ${StyledGrayContainer}
  padding: 8px 0px;
  width: 100%;
`;
